// Platform detection that works in both web and mobile environments
const isReactNative = typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
// Web implementation using localStorage
class WebTokenStorage {
    async getToken(key) {
        if (typeof window === 'undefined')
            return null;
        return localStorage.getItem(key);
    }
    async setToken(key, value) {
        if (typeof window === 'undefined')
            return;
        localStorage.setItem(key, value);
    }
    async removeToken(key) {
        if (typeof window === 'undefined')
            return;
        localStorage.removeItem(key);
    }
    async clear() {
        if (typeof window === 'undefined')
            return;
        localStorage.clear();
    }
}
// Mobile implementation using AsyncStorage
class MobileTokenStorage {
    constructor() {
        // Dynamically import AsyncStorage to avoid issues in web environment
        if (isReactNative) {
            try {
                this.AsyncStorage =
                    require('@react-native-async-storage/async-storage').default;
            }
            catch (error) {
                console.warn('AsyncStorage not available:', error);
            }
        }
    }
    async getToken(key) {
        if (!this.AsyncStorage)
            return null;
        try {
            return await this.AsyncStorage.getItem(key);
        }
        catch (error) {
            console.error('Error getting token from AsyncStorage:', error);
            return null;
        }
    }
    async setToken(key, value) {
        if (!this.AsyncStorage)
            return;
        try {
            await this.AsyncStorage.setItem(key, value);
        }
        catch (error) {
            console.error('Error setting token in AsyncStorage:', error);
        }
    }
    async removeToken(key) {
        if (!this.AsyncStorage)
            return;
        try {
            await this.AsyncStorage.removeItem(key);
        }
        catch (error) {
            console.error('Error removing token from AsyncStorage:', error);
        }
    }
    async clear() {
        if (!this.AsyncStorage)
            return;
        try {
            await this.AsyncStorage.clear();
        }
        catch (error) {
            console.error('Error clearing AsyncStorage:', error);
        }
    }
}
// Create platform-specific storage instance
export const tokenStorage = isReactNative
    ? new MobileTokenStorage()
    : new WebTokenStorage();
// Token keys
export const TOKEN_KEYS = {
    ACCESS_TOKEN: 'access_token',
    REFRESH_TOKEN: 'refresh_token',
    USER_DATA: 'user_data',
};
// Helper functions for common token operations
export const tokenUtils = {
    async getAccessToken() {
        return tokenStorage.getToken(TOKEN_KEYS.ACCESS_TOKEN);
    },
    async setAccessToken(token) {
        return tokenStorage.setToken(TOKEN_KEYS.ACCESS_TOKEN, token);
    },
    async getRefreshToken() {
        return tokenStorage.getToken(TOKEN_KEYS.REFRESH_TOKEN);
    },
    async setRefreshToken(token) {
        return tokenStorage.setToken(TOKEN_KEYS.REFRESH_TOKEN, token);
    },
    async getUserData() {
        const userData = await tokenStorage.getToken(TOKEN_KEYS.USER_DATA);
        return userData ? JSON.parse(userData) : null;
    },
    async setUserData(user) {
        return tokenStorage.setToken(TOKEN_KEYS.USER_DATA, JSON.stringify(user));
    },
    async clearAllTokens() {
        await Promise.all([
            tokenStorage.removeToken(TOKEN_KEYS.ACCESS_TOKEN),
            tokenStorage.removeToken(TOKEN_KEYS.REFRESH_TOKEN),
            tokenStorage.removeToken(TOKEN_KEYS.USER_DATA),
        ]);
    },
    // Check if token is expired (basic JWT parsing)
    isTokenExpired(token) {
        try {
            const parts = token.split('.');
            if (parts.length !== 3 || !parts[1])
                return true;
            const payload = JSON.parse(atob(parts[1]));
            const currentTime = Date.now() / 1000;
            return payload.exp < currentTime;
        }
        catch (error) {
            return true; // If we can't parse it, consider it expired
        }
    },
    // Get token expiration time
    getTokenExpiration(token) {
        try {
            const parts = token.split('.');
            if (parts.length !== 3 || !parts[1])
                return null;
            const payload = JSON.parse(atob(parts[1]));
            return payload.exp * 1000; // Convert to milliseconds
        }
        catch (error) {
            return null;
        }
    },
};
